import React, { useState } from "react";
import { Quote, Send, Loader2, Heart, HandHeart } from "lucide-react";
import { PageHero, SectionTitle } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { Honeypot } from "@/components/Honeypot";
import { VideoEmbed } from "@/components/VideoEmbed";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { STORIES, PRAYERS } from "@/lib/content";
import { IMG } from "@/lib/images";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Stories() {
  const tags = ["All", ...Array.from(new Set(STORIES.map((s) => s.tag)))];
  const [active, setActive] = useState("All");
  const list = active === "All" ? STORIES : STORIES.filter((s) => s.tag === active);

  const [form, setForm] = useState({ name: "", story: "", allow_publish: true, hp: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.story.trim()) { toast.error("Please share your name and a few words."); return; }
    setLoading(true);
    try {
      await api.post("/stories", form);
      toast.success("Thank you for trusting us with your story. 💛");
      setForm({ name: "", story: "", allow_publish: true, hp: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div data-testid="stories-page">
      <PageHero
        eyebrow="Survivor Stories"
        title="Light, told in first person"
        subtitle="These are real journeys through the dark toward something gentler. If you see yourself here, know that your next chapter is still unwritten."
        image={IMG.lakeDock}
        testid="stories-hero"
      />

      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-20">
        <div className="flex flex-wrap gap-2 justify-center mb-12">
          {tags.map((t) => (
            <button key={t} onClick={() => setActive(t)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${active === t ? "bg-bsn-midnight text-bsn-parchment" : "bg-white text-bsn-sapphire card-tactile hover:bg-bsn-canvas"}`}
              data-testid={`story-filter-${t.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}>
              {t}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {list.map((s, i) => (
            <Reveal key={s.id} delay={i * 80}>
              <article className="h-full bg-white rounded-2xl p-8 card-tactile flex flex-col" data-testid={`story-${s.id}`}>
                {s.videoUrl && <div className="mb-5 -mt-2"><VideoEmbed url={s.videoUrl} title={`${s.name}'s story`} /></div>}
                <Quote className="w-9 h-9 text-bsn-gold/60 mb-4" />
                <p className="font-serif text-2xl text-bsn-midnight leading-snug mb-4">“{s.quote}”</p>
                <p className="text-bsn-sapphire/80 leading-relaxed flex-1">{s.excerpt}</p>
                <div className="mt-6 pt-4 border-t border-bsn-gold/20 flex items-center justify-between">
                  <span className="font-semibold text-bsn-midnight">{s.name}</span>
                  <span className="text-[11px] font-mono uppercase tracking-wider text-bsn-bronze">{s.tag}</span>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Prayers & Blessings */}
      <section className="bg-bsn-canvas border-y border-bsn-gold/20" data-testid="prayers-section">
        <div className="max-w-5xl mx-auto px-4 py-16 sm:py-20">
          <SectionTitle eyebrow="Prayers & Blessings" title="Words and video reflections to hold onto" subtitle="Gentle prayers and shared blessings from our community. Video reflections appear here as they're shared." />
          <div className="grid md:grid-cols-2 gap-6">
            {PRAYERS.map((p, i) => (
              <Reveal key={p.id} delay={i * 90}>
                <div className="h-full bg-white rounded-2xl p-7 card-tactile flex flex-col" data-testid={`prayer-${p.id}`}>
                  {p.videoUrl && <div className="mb-5"><VideoEmbed url={p.videoUrl} title={p.title} /></div>}
                  <HandHeart className="w-8 h-8 text-bsn-gold mb-3" />
                  <h3 className="font-serif text-xl font-semibold text-bsn-midnight">{p.title}</h3>
                  <p className="text-bsn-sapphire/85 leading-relaxed mt-3 flex-1 italic">"{p.text}"</p>
                  <p className="text-xs font-mono uppercase tracking-wider text-bsn-bronze mt-4">{p.by}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-bsn-midnight text-bsn-parchment">
        <div className="max-w-3xl mx-auto px-4 py-16 sm:py-20">
          <div className="text-center mb-10">
            <Heart className="w-9 h-9 text-bsn-gold mx-auto mb-4" />
            <SectionTitle eyebrow="Share Your Hope" title="Your story could be someone's lifeline" subtitle="You choose whether your words are shared publicly. Take all the time you need." light />
          </div>
          <form onSubmit={submit} className="bg-bsn-navy/60 rounded-2xl p-6 sm:p-8 border border-bsn-gold/25 space-y-4" data-testid="story-submit-form">
            <Honeypot value={form.hp} onChange={(v) => setForm({ ...form, hp: v })} />
            <Input placeholder="Your name or chosen alias" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-bsn-midnight border-bsn-gold/30 text-bsn-parchment placeholder:text-bsn-ice/40" data-testid="story-name-input" />
            <Textarea rows={6} placeholder="Share as much or as little as feels right..." value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} className="bg-bsn-midnight border-bsn-gold/30 text-bsn-parchment placeholder:text-bsn-ice/40" data-testid="story-text-input" />
            <label className="flex items-center gap-3 text-sm text-bsn-ice/80 cursor-pointer">
              <Checkbox checked={form.allow_publish} onCheckedChange={(v) => setForm({ ...form, allow_publish: !!v })} data-testid="story-publish-checkbox" />
              I'm open to my story being shared anonymously to encourage others.
            </label>
            <Button type="submit" disabled={loading} className="w-full btn-gold font-semibold h-12" data-testid="story-submit-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Share My Story</>}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
